import React, { useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { PackageX, TriangleAlert, Truck, Search } from "lucide-react";
import {
  InventorySnapshotData,
  InventoryStatusCard,
  InventoryStatusTone,
  InventoryVendor,
} from "./types";
import { DashboardSectionDivider, useActivateDashboardSection } from "../DashboardSectionDivider";
import {
  dashboardInteractiveCardSx,
  dashboardPanelSx,
} from "../dashboardSurfaceStyles";

interface InventorySnapshotProps {
  data: InventorySnapshotData;
  variant?: "sales" | "service";
  onCardClick?: (cardId: string) => void;
}

const VARIANT_CONFIG = {
  sales: {
    sectionOverviewTitle: "Dispatch & Vendor Overview",
    sectionOverviewSubtitle:
      "Dispatch packing status and vendor purchase delivery metrics",
    leftPanelTitle: "Dispatch & Packing",
    rightPanelTitle: "Vendor Purchase Overview",
    entityLabel: "Vendor",
    searchPlaceholder: "Search vendor...",
    metricLabels: {
      po_month: "Purchase Order Placed This Month",
      pending: "Pending Vendor Deliveries",
      late: "Late Vendors Deliveries",
    },
  },
  service: {
    sectionOverviewTitle: "Challan & Warehouse Overview",
    sectionOverviewSubtitle:
      "Challan returns and warehouse accessory flow for the selected period",
    leftPanelTitle: "Challan & Returns",
    rightPanelTitle: "Warehouse Overview",
    entityLabel: "Warehouse",
    searchPlaceholder: "Search warehouse...",
    metricLabels: {
      po_month: "Challans Created This Period",
      pending: "Pending Accessory Returns",
      late: "Returns Inwarded This Period",
    },
  },
} as const;

const TONE: Record<
  InventoryStatusTone,
  { accent: string; bg: string; footerBg: string }
> = {
  critical: { accent: "#d2363a", bg: "#fff1f1", footerBg: "#ffe8e8" },
  warning: { accent: "#e67e22", bg: "#fff7ed", footerBg: "#ffecd9" },
  success: { accent: "#0f9f6e", bg: "#ecfdf5", footerBg: "#dcfce7" },
};

const ICONS: Record<string, React.ReactNode> = {
  out_of_stock: <PackageX size={16} strokeWidth={2} />,
  low_stock: <TriangleAlert size={16} strokeWidth={2} />,
  incoming_stock: <Truck size={16} strokeWidth={2} />,
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const autocompleteSx = {
  width: { xs: "100%", sm: 240 },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "11.5px",
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    minHeight: 32,
    py: 0,
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#d2363a88" },
    "&.Mui-focused fieldset": {
      borderColor: "#d2363a",
      borderWidth: "1.5px",
    },
  },
};

const listboxPaperSx = {
  mt: 0.5,
  borderRadius: "10px",
  border: "1px solid #e8eaed",
  boxShadow: "0 8px 20px rgba(17,24,39,0.1)",
  overflow: "hidden",
  "& .MuiAutocomplete-listbox": {
    py: 0.4,
    maxHeight: 220,
  },
};

const AvatarChip: React.FC<{ name: string }> = ({ name }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontFamily: "'Poppins', sans-serif",
      fontSize: "9px",
      fontWeight: 700,
      color: "#d2363a",
      backgroundColor: "#fff1f1",
    }}
  >
    {getInitials(name)}
  </Box>
);

const StatusCard: React.FC<{ card: InventoryStatusCard; onCardClick?: (id: string) => void }> = ({ card, onCardClick }) => {
  const tone = TONE[card.tone];
  const isClickable = card.id === "out_of_stock" || card.id === "low_stock" || card.id === "incoming_stock";

  return (
    <Box
      onClick={() => isClickable && onCardClick?.(card.id)}
      sx={{
        ...dashboardInteractiveCardSx({
          accentBorder: tone.accent,
          clickable: isClickable,
        }),
        borderRadius: "10px",
        overflow: "visible",
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: tone.bg,
              color: tone.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 3px 8px ${tone.accent}33`,
              transform: "translateZ(10px)",
            }}
          >
            {ICONS[card.id]}
          </Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {card.label}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.1,
          }}
        >
          {card.value}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "11px",
            color: "#6b7280",
            mt: 0.4,
          }}
        >
          {card.helperText}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.7,
          backgroundColor: tone.footerBg,
          borderTop: `1px solid ${tone.accent}33`,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "11px",
            fontWeight: 600,
            color: tone.accent,
          }}
        >
          {card.footerText}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricRow: React.FC<{
  label: string;
  count: number;
  maxCount: number;
}> = ({ label, count, maxCount }) => {
  const widthPct =
    maxCount > 0 ? Math.max(18, Math.round((count / maxCount) * 100)) : 18;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.55 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            color: "#374151",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            fontWeight: 700,
            color: "#111827",
            minWidth: 28,
            textAlign: "right",
          }}
        >
          {count}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 7,
          borderRadius: 999,
          backgroundColor: "#f3f4f6",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${widthPct}%`,
            borderRadius: 999,
            background: "linear-gradient(90deg, #d2363a 0%, #ef6b6e 100%)",
          }}
        />
      </Box>
    </Box>
  );
};

const OverviewPanel: React.FC<{
  title: string;
  headerRight?: React.ReactNode;
  rows: Array<{ id: string; label: string; count: number }>;
}> = ({ title, headerRight, rows }) => {
  const maxCount = Math.max(...rows.map((row) => row.count), 1);

  return (
    <Box
      sx={{
        ...dashboardPanelSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 46,
          px: 1.5,
          borderBottom: "1px solid #eef0f3",
          backgroundColor: "#fafbfc",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {headerRight}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 1.4,
          p: 1.5,
        }}
      >
        {rows.map((row) => (
          <MetricRow
            key={row.id}
            label={row.label}
            count={row.count}
            maxCount={maxCount}
          />
        ))}
      </Box>
    </Box>
  );
};

export const InventorySnapshot: React.FC<InventorySnapshotProps> = ({
  data,
  variant = "sales",
  onCardClick,
}) => {
  const { activateSection } = useActivateDashboardSection();
  const config = VARIANT_CONFIG[variant];
  const [selectedVendorId, setSelectedVendorId] = useState(
    data.vendors[0]?.id || "",
  );

  const selectedVendor =
    data.vendors.find((vendor) => vendor.id === selectedVendorId) || null;

  const dispatchRows = useMemo(
    () =>
      data.dispatchPacking.map((row) => ({
        id: row.id,
        label: row.label
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        count: row.count,
      })),
    [data.dispatchPacking],
  );

  const vendorRows = useMemo(() => {
    if (!selectedVendor) return [];
    return [
      {
        id: "po_month",
        label: config.metricLabels.po_month,
        count: selectedVendor.metrics.purchaseOrdersThisMonth,
      },
      {
        id: "pending",
        label: config.metricLabels.pending,
        count: selectedVendor.metrics.pendingVendorDeliveries,
      },
      {
        id: "late",
        label: config.metricLabels.late,
        count: selectedVendor.metrics.lateVendorDeliveries,
      },
    ];
  }, [config.metricLabels, selectedVendor]);

  const renderVendorOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: InventoryVendor,
  ) => {
    const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
      key?: string;
    };
    return (
      <Box
        component="li"
        key={key}
        {...optionProps}
        sx={{
          display: "flex !important",
          alignItems: "center",
          gap: 0.85,
          px: "8px !important",
          py: "6px !important",
          mx: 0.4,
          borderRadius: "8px",
          "&.Mui-focused": { backgroundColor: "#fff5f5 !important" },
          '&[aria-selected="true"]': { backgroundColor: "#fef2f2 !important" },
        }}
      >
        <AvatarChip name={option.name} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11.5px",
              fontWeight: 600,
              color: "#111827",
              lineHeight: 1.2,
            }}
          >
            {option.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "9.5px",
              color: "#6b7280",
              mt: 0.1,
            }}
          >
            {config.entityLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2.75 }} onClickCapture={() => activateSection("inventory")}>
      <DashboardSectionDivider sectionId="inventory" />
      <Box sx={{ mb: 1.25 }}>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Inventory Snapshot
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: 1.25,
          mb: 1.5,
        }}
      >
        {data.statusCards.map((card) => (
          <StatusCard key={card.id} card={card} onCardClick={onCardClick} />
        ))}
      </Box>

      <Box
        sx={{
          mt: 2.5,
          mb: 1.25,
        }}
        onClickCapture={() => activateSection("inventory-overview")}
      >
        <DashboardSectionDivider sectionId="inventory-overview" />
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          {config.sectionOverviewTitle}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "12px",
            color: "#6b7280",
            mt: 0.25,
          }}
        >
          {config.sectionOverviewSubtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1.25,
          alignItems: "stretch",
        }}
      >
        <OverviewPanel title={config.leftPanelTitle} rows={dispatchRows} />

        <OverviewPanel
          title={config.rightPanelTitle}
          headerRight={
            <Autocomplete
              size="small"
              options={data.vendors}
              value={selectedVendor}
              onChange={(_event, value: InventoryVendor | null) => {
                if (value) setSelectedVendorId(value.id);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter((option) =>
                  option.name.toLowerCase().includes(query),
                );
              }}
              popupIcon={null}
              clearOnBlur={false}
              sx={autocompleteSx}
              slotProps={{ paper: { sx: listboxPaperSx } }}
              renderOption={renderVendorOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={config.searchPlaceholder}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "#9ca3af",
                            pl: 0.5,
                            pr: 0.25,
                          }}
                        >
                          <Search size={13} />
                        </Box>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          }
          rows={vendorRows}
        />
      </Box>
    </Box>
  );
};
